import React, { useImperativeHandle, useRef } from 'react';
import { useReactive } from 'ahooks';
import { Button, Dropdown } from 'kenshin';
import {
  SearchOutlined,
  AppstoreOutlined,
  DownloadOutlined,
  CopyOutlined,
  StarOutlined,
  EditOutlined,
  DeleteOutlined,
  PictureOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import InputArea from '../InputArea';
import ImagePreview from '../ImagePreview';
import VideoPreview from '../VideoPreview';
import styles from './index.less';

const MODES = {
  CHAT: 'chat',
  IMAGE: 'image',
  VIDEO: 'video',
};

// 模拟消息数据
const mockMessages = [
  {
    id: '1',
    role: 'user',
    content: '生成一张端午节海报图片',
    time: '2026-06-29 10:00',
  },
  {
    id: '2',
    role: 'assistant',
    content: '好嘞！这就为您绘制一张充满节日氛围的端午节海报，瞧好吧！',
    time: '2026-06-29 10:01',
    images: [
      'https://picsum.photos/seed/dw1/400/600',
      'https://picsum.photos/seed/dw2/400/600',
      'https://picsum.photos/seed/dw3/400/600',
      'https://picsum.photos/seed/dw4/400/600',
    ],
  },
];

const ChatArea = React.forwardRef((props, ref) => {
  const { mode, conversationId, onModeChange, onToggleAssetLibrary } = props;
  const messagesEndRef = useRef();

  const state = useReactive({
    messages: [...mockMessages],
    loading: false,
    previewImage: null,
    previewVideo: null,
  });

  useImperativeHandle(ref, () => ({
    clearMessages: () => {
      state.messages = [];
    },
    addMessage: (msg) => {
      state.messages = [...state.messages, msg];
    },
  }));

  // 发送消息
  const handleSend = (data) => {
    const userMsg = {
      id: Date.now().toString(),
      role: 'user',
      content: data.text,
      time: new Date().toLocaleString(),
      images: data.images || [],
    };
    state.messages = [...state.messages, userMsg];
    state.loading = true;

    // 模拟AI回复
    setTimeout(() => {
      const assistantMsg = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: '好嘞！正在按照您的要求生成图片。',
        time: new Date().toLocaleString(),
        images: mode === MODES.IMAGE ? [
          'https://picsum.photos/seed/new1/400/600',
          'https://picsum.photos/seed/new2/400/600',
        ] : mode === MODES.VIDEO ? [] : [],
        video: mode === MODES.VIDEO ? 'https://example.com/video.mp4' : null,
      };
      state.messages = [...state.messages, assistantMsg];
      state.loading = false;
    }, 2000);
  };

  // 打开图片预览
  const handlePreviewImage = (imgUrl) => {
    state.previewImage = imgUrl;
  };

  // 打开视频预览
  const handlePreviewVideo = (videoUrl) => {
    state.previewVideo = videoUrl;
  };

  // 消息操作菜单
  const getMessageMenu = (msg) => (
    <Dropdown.Menu>
      <Dropdown.Item>
        <div>复制</div>
      </Dropdown.Item>
      <Dropdown.Item>
        <div>收藏</div>
      </Dropdown.Item>
      <Dropdown.Item>
        <div style={{ color: '#ff4d4f' }}>删除</div>
      </Dropdown.Item>
    </Dropdown.Menu>
  );

  // 渲染消息内容
  const renderMessageContent = (msg) => {
    if (msg.role === 'user') {
      return (
        <div className={styles.userMessage}>
          <div className={styles.msgContent}>{msg.content}</div>
          {msg.images && msg.images.length > 0 && (
            <div className={styles.msgImages}>
              {msg.images.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt=""
                  className={styles.msgImage}
                  onClick={() => handlePreviewImage(img)}
                />
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={styles.assistantMessage}>
        <div className={styles.msgContent}>{msg.content}</div>
        {msg.images && msg.images.length > 0 && (
          <div className={styles.msgImageGrid}>
            {msg.images.map((img, idx) => (
              <div
                key={idx}
                className={styles.msgImageItem}
                onClick={() => handlePreviewImage(img)}
              >
                <img src={img} alt="" />
                <div className={styles.msgImageActions}>
                  <DownloadOutlined title="下载" />
                  <CopyOutlined title="复制" />
                  <StarOutlined title="收藏" />
                  <EditOutlined title="编辑" />
                  <DeleteOutlined title="删除" />
                </div>
              </div>
            ))}
          </div>
        )}
        {msg.video && (
          <div className={styles.msgVideo} onClick={() => handlePreviewVideo(msg.video)}>
            <VideoCameraOutlined style={{ fontSize: 48, color: '#666' }} />
            <div>点击播放视频</div>
          </div>
        )}
        {/* <Dropdown overlay={getMessageMenu(msg)} trigger="click">
          <div className={styles.msgActions}>
            <CopyOutlined />
            <StarOutlined />
            <DeleteOutlined />
          </div>
        </Dropdown> */}
      </div>
    );
  };

  return (
    <div className={styles.chatArea}>
      {/* 顶部工具栏 */}
      <div className={styles.chatHeader}>
        <div className={styles.headerLeft}>
          <SearchOutlined className={styles.headerIcon} />
          <AppstoreOutlined className={styles.headerIcon} onClick={onToggleAssetLibrary} />
        </div>
      </div>

      {/* 消息列表 */}
      <div className={styles.messageList}>
        {state.messages.length === 0 && !state.loading && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <PictureOutlined style={{ fontSize: 64, color: '#cbd5e1' }} />
            </div>
            <div className={styles.emptyText}>欢迎使用AI创作</div>
            <div className={styles.emptySubText}>选择下方模式，开始创作吧</div>
          </div>
        )}

        {state.messages.map((msg) => (
          <div key={msg.id} className={styles.messageWrapper}>
            {renderMessageContent(msg)}
          </div>
        ))}

        {state.loading && (
          <div className={styles.loadingState}>
            <div className={styles.loadingDot} />
            <div className={styles.loadingDot} />
            <div className={styles.loadingDot} />
            <span className={styles.loadingText}>生成中...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* 底部输入区 */}
      <InputArea mode={mode} onSend={handleSend} />

      {/* 图片预览弹窗 */}
      {state.previewImage && (
        <ImagePreview
          imageUrl={state.previewImage}
          onClose={() => (state.previewImage = null)}
        />
      )}

      {/* 视频预览弹窗 */}
      {state.previewVideo && (
        <VideoPreview
          videoUrl={state.previewVideo}
          onClose={() => (state.previewVideo = null)}
        />
      )}
    </div>
  );
});

export default ChatArea;
